import { useVisits } from '../hooks/useVisits';
import { useUser } from '../../../features/settings/hooks/useUser';
import { useCancelVisit } from '../hooks/useCancelVisit';
import { Card, CardContent } from '../../../components/ui/card';
import { format } from 'date-fns';
import { Badge } from '../../../components/ui/badge';
import { Button } from '../../../components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../../components/ui/alert-dialog';
import { useState } from 'react';
import { CalendarIcon, Clock } from 'lucide-react';

interface PatientFollowUpsProps {
  patientId: string;
}

export function PatientFollowUps({ patientId }: PatientFollowUpsProps) {
  const { data: visits = [], isLoading } = useVisits({ 
    patientId,
    status: "scheduled",
    reason: "Follow-up"
  });
  const cancelVisit = useCancelVisit();
  const [visitToCancel, setVisitToCancel] = useState<string | null>(null);

  if (isLoading) {
    return (
      <div className="space-y-3">
        {[1, 2].map((i) => (
          <Card key={i} className="border shadow-sm">
            <CardContent className="p-4">
              <div className="space-y-3 animate-pulse">
                <div className="h-4 bg-muted rounded w-3/4"></div>
                <div className="h-4 bg-muted rounded w-1/2"></div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (visits.length === 0) {
    return (
      <Card className="border-dashed">
        <CardContent className="p-6">
          <div className="flex flex-col items-center justify-center text-center space-y-2">
            <CalendarIcon className="h-8 w-8 text-muted-foreground/60" />
            <p className="text-sm text-muted-foreground">No pending follow-ups</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-3">
      {visits.map((visit) => (
        <Card key={visit.id} className="border shadow-sm hover:shadow-md transition-shadow">
          <CardContent className="p-4">
            <div className="space-y-4">
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <h4 className="font-semibold">
                      Follow-up Visit
                    </h4>
                    {visit.isUrgent && (
                      <Badge variant="destructive" className="uppercase">Urgent</Badge>
                    )}
                  </div>
                  <div className="mt-1 flex items-center gap-3 text-sm text-muted-foreground">
                    <div className="flex items-center gap-1">
                      <CalendarIcon className="h-4 w-4" />
                      {format(new Date(visit.startTime), 'MMM d, yyyy')}
                    </div>
                    <div className="flex items-center gap-1">
                      <Clock className="h-4 w-4" />
                      {format(new Date(visit.startTime), 'h:mm a')}
                    </div>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-destructive hover:text-destructive hover:bg-destructive/10"
                  onClick={() => setVisitToCancel(visit.id)}
                >
                  Cancel
                </Button>
              </div>

              <div className="grid gap-3 text-sm">
                <PhysicianInfo doctorId={visit.doctorId} />
                
                {visit.initialNotes && (
                  <div>
                    <p className="font-medium text-muted-foreground">Notes</p>
                    <p className="mt-0.5 whitespace-pre-wrap">{visit.initialNotes}</p>
                  </div>
                )}

                {visit.diagnosis && (
                  <div>
                    <p className="font-medium text-muted-foreground">Original Diagnosis</p>
                    <p className="mt-0.5">{visit.diagnosis}</p>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      ))}

      <AlertDialog open={!!visitToCancel} onOpenChange={() => setVisitToCancel(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel Follow-up Visit</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to cancel this follow-up visit? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep Visit</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (visitToCancel) {
                  cancelVisit.mutate(visitToCancel);
                  setVisitToCancel(null);
                }
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Cancel Visit
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function PhysicianInfo({ doctorId }: { doctorId: string }) {
  const { data: physician } = useUser(doctorId);
  
  if (!physician) return null;
  
  return (
    <div>
      <p className="font-medium text-muted-foreground">Physician</p>
      <p className="mt-0.5">
        {physician.title} {physician.firstName} {physician.lastName}
        {physician.specialization && (
          <span className="text-muted-foreground"> • {physician.specialization}</span>
        )}
      </p>
    </div>
  );
}
